/**
 * Agent Analysis Component
 * Displays disinformation analysis with progressive updates
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { agentService } from '../../services/agent.service';
import { localized } from '../../utils/i18n-fields';
import type {
  AnalysisProgress,
  AnalysisResponse,
  DetectedTechnique,
  VerifiedClaim,
  AssessedSource,
  FinalVerdict,
} from '../../services/agent.service';

// =============================================================================
// Styled Components
// =============================================================================

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const InputSection = styled.div`
  margin-bottom: 24px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const AnalyzeButton = styled.button<{ disabled?: boolean }>`
  padding: 12px 32px;
  background: ${props => props.disabled ? '#94a3b8' : '#3b82f6'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s;
  margin-top: 12px;

  &:hover:not(:disabled) {
    background: #2563eb;
  }
`;

// Progress Section
const ProgressSection = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ProgressTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #1e293b;
`;

const ProgressStatus = styled.span<{ status: string }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#dcfce7';
      case 'failed': return '#fee2e2';
      case 'processing': return '#dbeafe';
      default: return '#f1f5f9';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#166534';
      case 'failed': return '#dc2626';
      case 'processing': return '#1d4ed8';
      default: return '#64748b';
    }
  }};
`;

const StepsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const StepBadge = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#dcfce7';
      case 'running': return '#dbeafe';
      case 'failed': return '#fee2e2';
      default: return '#f1f5f9';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#166534';
      case 'running': return '#1d4ed8';
      case 'failed': return '#dc2626';
      default: return '#94a3b8';
    }
  }};
  animation: ${props => props.status === 'running' ? pulse : 'none'} 1.5s infinite;
`;

const StepIcon = styled.span`
  font-size: 14px;
`;

const StepDuration = styled.span`
  font-size: 11px;
  opacity: 0.7;
  margin-left: 4px;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percent: number; status: string }>`
  height: 100%;
  width: ${props => props.percent}%;
  background: ${props => props.status === 'failed' ? '#ef4444' : '#3b82f6'};
  transition: width 0.3s ease;
`;

const TimeInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
`;

// Results Section
const ResultsSection = styled.div`
  margin-top: 24px;
`;

const VerdictCard = styled.div<{ color: string }>`
  background: ${props => {
    switch (props.color) {
      case 'red': return 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)';
      case 'orange': return 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)';
      case 'yellow': return 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)';
      case 'green': return 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)';
      default: return 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)';
    }
  }};
  border-left: 4px solid ${props => {
    switch (props.color) {
      case 'red': return '#ef4444';
      case 'orange': return '#f97316';
      case 'yellow': return '#eab308';
      case 'green': return '#22c55e';
      default: return '#94a3b8';
    }
  }};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const VerdictHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const VerdictInfo = styled.div``;

const VerdictLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 4px;
`;

const VerdictCode = styled.h2<{ color: string }>`
  margin: 0;
  font-size: 28px;
  color: ${props => {
    switch (props.color) {
      case 'red': return '#dc2626';
      case 'orange': return '#ea580c';
      case 'yellow': return '#ca8a04';
      case 'green': return '#16a34a';
      default: return '#475569';
    }
  }};
`;

const VerdictName = styled.div`
  font-size: 15px;
  color: #475569;
  margin-top: 4px;
`;

const ScoreCircle = styled.div<{ score: number }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${props => {
    if (props.score >= 80) return '#fef2f2';
    if (props.score >= 60) return '#fff7ed';
    if (props.score >= 40) return '#fefce8';
    return '#f0fdf4';
  }};
  border: 3px solid ${props => {
    if (props.score >= 80) return '#ef4444';
    if (props.score >= 60) return '#f97316';
    if (props.score >= 40) return '#eab308';
    return '#22c55e';
  }};
`;

const ScoreValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
`;

const ScoreLabel = styled.div`
  font-size: 11px;
  color: #64748b;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetaIcon = styled.span`
  font-size: 16px;
`;

const MetaText = styled.span`
  font-size: 14px;
  color: #475569;
`;

// Section Cards
const SectionCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const SectionHeader = styled.div<{ expanded?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  background: ${props => props.expanded ? '#f8fafc' : 'white'};
  transition: background 0.2s;

  &:hover {
    background: #f8fafc;
  }
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionBadge = styled.span<{ color?: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.color || '#f1f5f9'};
  color: ${props => props.color ? 'white' : '#64748b'};
`;

const SectionContent = styled.div<{ expanded: boolean }>`
  padding: ${props => props.expanded ? '16px 20px' : '0 20px'};
  max-height: ${props => props.expanded ? '2000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  border-top: ${props => props.expanded ? '1px solid #e2e8f0' : 'none'};
`;

const ExpandIcon = styled.span<{ expanded: boolean }>`
  transition: transform 0.2s;
  transform: rotate(${props => props.expanded ? '180deg' : '0deg'});
`;

// Technique Item
const TechniqueItem = styled.div`
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TechniqueHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const TechniqueName = styled.span`
  font-weight: 600;
  color: #1e293b;
`;

const TechniqueConfidence = styled.span<{ value: number }>`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.value >= 80 ? '#16a34a' : props.value >= 60 ? '#ca8a04' : '#64748b'};
`;

const TechniqueEvidence = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
`;

const TechniqueMeta = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  font-size: 12px;
  color: #94a3b8;
`;

// Claim Item
const ClaimItem = styled.div`
  padding: 12px 16px;
  border-left: 3px solid;
  border-left-color: ${props => props.color || '#94a3b8'};
  background: #f8fafc;
  border-radius: 0 8px 8px 0;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ClaimText = styled.p`
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #1e293b;
  line-height: 1.5;
`;

const ClaimStatus = styled.span<{ color: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.color) {
      case 'red': return '#fee2e2';
      case 'orange': return '#ffedd5';
      case 'green': return '#dcfce7';
      default: return '#f1f5f9';
    }
  }};
  color: ${props => {
    switch (props.color) {
      case 'red': return '#dc2626';
      case 'orange': return '#ea580c';
      case 'green': return '#16a34a';
      default: return '#64748b';
    }
  }};
`;

// Source Item
const SourceItem = styled.div`
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SourceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SourceDomain = styled.span`
  font-weight: 600;
  color: #1e293b;
`;

const SourceScore = styled.span<{ score: number }>`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.score >= 50 ? '#16a34a' : props.score >= 30 ? '#ca8a04' : '#dc2626'};
`;

const RedFlagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const RedFlag = styled.span`
  padding: 4px 8px;
  background: #fee2e2;
  color: #dc2626;
  font-size: 11px;
  border-radius: 4px;
`;

const ErrorMessage = styled.div`
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  margin-top: 16px;
`;

// =============================================================================
// Component
// =============================================================================

interface AgentAnalysisProps {
  onAnalysisComplete?: (result: AnalysisResponse) => void;
}

export const AgentAnalysis: React.FC<AgentAnalysisProps> = ({ onAnalysisComplete }) => {
  const [inputText, setInputText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState<AnalysisProgress | null>(null);
  const [result, setResult] = useState<AnalysisResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    techniques: true,
    claims: true,
    sources: false,
    dimensions: false,
  });

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleAnalyze = useCallback(async () => {
    if (!inputText.trim()) return;

    setIsAnalyzing(true);
    setError(null);
    setProgress(null);
    setResult(null);

    const sessionId = agentService.generateSessionId();
    sessionIdRef.current = sessionId;

    // Track last progress to avoid unnecessary re-renders
    let lastProgressJson = "";

    const startProgressPolling = () => {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const progressData = await agentService.getProgress(sessionId);
          if (progressData) {
            const currentJson = JSON.stringify(progressData);
            if (currentJson !== lastProgressJson) {
              lastProgressJson = currentJson;
              setProgress(progressData);
            }

            if (progressData.status === 'completed' || progressData.status === 'failed') {
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
            }
          }
        } catch (err) {
          console.error('Progress poll error:', err);
        }
      }, 2000);
    };

    try {
      // Start analysis — poll AFTER request is sent so session exists on server
      const analysisPromise = agentService.analyzeText(inputText, {
        sessionId,
        pipeline: 'disinformation',
      });

      // Start polling after a short delay to let the server create the session
      startProgressPolling();

      const analysisResult = await analysisPromise;

      // Clear polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      if (analysisResult.success) {
        setResult(analysisResult);
        onAnalysisComplete?.(analysisResult);
      } else {
        setError(analysisResult.error || 'Analysis failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  }, [inputText, onAnalysisComplete]);

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✓';
      case 'running': return '●';
      case 'failed': return '✕';
      default: return '○';
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getProgressPercent = () => {
    if (!progress) return 0;
    const completed = progress.steps.filter(s => s.status === 'completed').length;
    return Math.round((completed / progress.steps.length) * 100);
  };

  // Get data to display (from progress partial results or final result)
  const techniques: DetectedTechnique[] =
    result?.analysis?.techniques || progress?.partialResults?.techniques || [];
  const claims: VerifiedClaim[] =
    result?.analysis?.verified_claims || progress?.partialResults?.claims || [];
  const sources: AssessedSource[] =
    result?.analysis?.sources || progress?.partialResults?.sources || [];
  const verdict: FinalVerdict | undefined =
    result?.analysis?.verdict || progress?.partialResults?.verdict;

  return (
    <Container>
      {/* Input Section */}
      <InputSection>
        <TextArea
          placeholder="Introdu textul de analizat pentru dezinformare..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          disabled={isAnalyzing}
        />
        <AnalyzeButton
          onClick={handleAnalyze}
          disabled={isAnalyzing || !inputText.trim()}
        >
          {isAnalyzing ? 'Analizez...' : 'Analizează'}
        </AnalyzeButton>
      </InputSection>

      {/* Progress Section */}
      {(isAnalyzing || progress) && (
        <ProgressSection>
          <ProgressHeader>
            <ProgressTitle>Progres Analiză</ProgressTitle>
            <ProgressStatus status={progress?.status || 'pending'}>
              {progress?.status === 'completed' ? 'Complet' :
               progress?.status === 'failed' ? 'Eșuat' :
               progress?.status === 'processing' ? 'În procesare' : 'Așteptare'}
            </ProgressStatus>
          </ProgressHeader>

          <StepsContainer>
            {progress?.steps.map((step) => (
              <StepBadge key={step.id} status={step.status}>
                <StepIcon>{getStepIcon(step.status)}</StepIcon>
                {step.nameRo || step.name}
                {step.count !== undefined && step.count !== null && ` (${step.count})`}
                {step.durationMs !== undefined && (
                  <StepDuration>{formatDuration(step.durationMs)}</StepDuration>
                )}
              </StepBadge>
            ))}
          </StepsContainer>

          <ProgressBar>
            <ProgressFill
              percent={getProgressPercent()}
              status={progress?.status || 'pending'}
            />
          </ProgressBar>

          <TimeInfo>
            <span>{getProgressPercent()}% complet</span>
            {progress?.totalDurationMs && (
              <span>Durată totală: {formatDuration(progress.totalDurationMs)}</span>
            )}
          </TimeInfo>
        </ProgressSection>
      )}

      {/* Error Message */}
      {error && (
        <ErrorMessage>
          Eroare: {error}
        </ErrorMessage>
      )}

      {/* Results Section */}
      {(verdict || techniques.length > 0 || claims.length > 0) && (
        <ResultsSection>
          {/* Verdict Card */}
          {verdict && (
            <VerdictCard color={verdict.verdict_color}>
              <VerdictHeader>
                <VerdictInfo>
                  <VerdictLabel>Verdict</VerdictLabel>
                  <VerdictCode color={verdict.verdict_color}>
                    {verdict.verdict_code}
                  </VerdictCode>
                  <VerdictName>{verdict.verdict_name}</VerdictName>
                </VerdictInfo>
                <ScoreCircle score={verdict.score}>
                  <ScoreValue>{verdict.score}</ScoreValue>
                  <ScoreLabel>/100</ScoreLabel>
                </ScoreCircle>
              </VerdictHeader>
              <MetaRow>
                <MetaItem>
                  <MetaIcon>🎯</MetaIcon>
                  <MetaText>Confidență: {verdict.confidence.level} ({Math.round(verdict.confidence.score)}%)</MetaText>
                </MetaItem>
                <MetaItem>
                  <MetaIcon>⚠️</MetaIcon>
                  <MetaText>Risc: {verdict.risk.name}</MetaText>
                </MetaItem>
              </MetaRow>
            </VerdictCard>
          )}

          {/* Techniques Section */}
          {techniques.length > 0 && (
            <SectionCard>
              <SectionHeader
                expanded={expandedSections.techniques}
                onClick={() => toggleSection('techniques')}
              >
                <SectionTitle>
                  🔍 Tehnici de Manipulare
                  <SectionBadge color="#ef4444">{techniques.length}</SectionBadge>
                </SectionTitle>
                <ExpandIcon expanded={expandedSections.techniques}>▼</ExpandIcon>
              </SectionHeader>
              <SectionContent expanded={expandedSections.techniques}>
                {techniques.map((tech, idx) => (
                  <TechniqueItem key={idx}>
                    <TechniqueHeader>
                      <TechniqueName>{localized(tech, 'technique_name')}</TechniqueName>
                      <TechniqueConfidence value={tech.confidence}>
                        {tech.confidence}% confidență
                      </TechniqueConfidence>
                    </TechniqueHeader>
                    <TechniqueEvidence>{tech.evidence}</TechniqueEvidence>
                    <TechniqueMeta>
                      <span>Dimensiune: {tech.dimension_code}</span>
                      <span>Severitate: {tech.severity}%</span>
                    </TechniqueMeta>
                  </TechniqueItem>
                ))}
              </SectionContent>
            </SectionCard>
          )}

          {/* Claims Section */}
          {claims.length > 0 && (
            <SectionCard>
              <SectionHeader
                expanded={expandedSections.claims}
                onClick={() => toggleSection('claims')}
              >
                <SectionTitle>
                  📋 Afirmații Verificate
                  <SectionBadge color="#3b82f6">{claims.length}</SectionBadge>
                </SectionTitle>
                <ExpandIcon expanded={expandedSections.claims}>▼</ExpandIcon>
              </SectionHeader>
              <SectionContent expanded={expandedSections.claims}>
                {claims.map((claim, idx) => (
                  <ClaimItem key={idx} color={claim.status_color}>
                    <ClaimText>"{claim.text}"</ClaimText>
                    <ClaimStatus color={claim.status_color}>
                      {localized(claim, 'status_name')} ({claim.confidence}%)
                    </ClaimStatus>
                  </ClaimItem>
                ))}
              </SectionContent>
            </SectionCard>
          )}

          {/* Sources Section */}
          {sources.length > 0 && (
            <SectionCard>
              <SectionHeader
                expanded={expandedSections.sources}
                onClick={() => toggleSection('sources')}
              >
                <SectionTitle>
                  🌐 Surse Evaluate
                  <SectionBadge>{sources.length}</SectionBadge>
                </SectionTitle>
                <ExpandIcon expanded={expandedSections.sources}>▼</ExpandIcon>
              </SectionHeader>
              <SectionContent expanded={expandedSections.sources}>
                {sources.map((source, idx) => (
                  <SourceItem key={idx}>
                    <SourceHeader>
                      <SourceDomain>{source.domain}</SourceDomain>
                      <SourceScore score={source.final_score}>
                        {source.final_score}/100
                      </SourceScore>
                    </SourceHeader>
                    {source.red_flags && source.red_flags.length > 0 && (
                      <RedFlagsList>
                        {source.red_flags.map((flag, flagIdx) => (
                          <RedFlag key={flagIdx}>
                            {typeof flag === 'string' ? flag : flag.flag}
                          </RedFlag>
                        ))}
                      </RedFlagsList>
                    )}
                  </SourceItem>
                ))}
              </SectionContent>
            </SectionCard>
          )}

          {/* Meta Info */}
          {result?.meta && (
            <SectionCard>
              <SectionHeader
                expanded={expandedSections.meta}
                onClick={() => toggleSection('meta')}
              >
                <SectionTitle>
                  ⚙️ Informații Analiză
                </SectionTitle>
                <ExpandIcon expanded={expandedSections.meta || false}>▼</ExpandIcon>
              </SectionHeader>
              <SectionContent expanded={expandedSections.meta || false}>
                <MetaRow>
                  <MetaItem>
                    <MetaText>Durată: {formatDuration(result.meta.durationMs)}</MetaText>
                  </MetaItem>
                  {result.meta.model && (
                    <MetaItem>
                      <MetaText>Model: {result.meta.model}</MetaText>
                    </MetaItem>
                  )}
                  {result.meta.paramsUsed && (
                    <MetaItem>
                      <MetaText>Parametri DB: ✓</MetaText>
                    </MetaItem>
                  )}
                </MetaRow>
              </SectionContent>
            </SectionCard>
          )}
        </ResultsSection>
      )}
    </Container>
  );
};

export default AgentAnalysis;
